<template>
  <ts-modal
    ref="modal"
    :modal-title="modalTitle"
    modal-class="modal-form"
    modal-dialog-class="xlsx-upload-modal"
  >
    <template #modal-content>
      <div
        v-if="error"
        class="container mt-3"
      >
        <alert
          :dismissable="true"
          :message="error"
          type="danger"
          @dismiss="error = null"
        />
      </div>
      <slot
        :show-upload="showWelcome"
        name="before-upload"
      />
      <div
        v-if="showWelcome"
        class="upload-container"
      >
        <div class="upload-row">
          <div class="upload-col tight button">
            <label class="btn btn-sm btn-primary">
              <span class="glyphicons icon-left upload align-middle" /><span class="align-middle">Select file to upload</span>
              <input
                ref="importFileField"
                style="display: none"
                type="file"
                @change="uploadCandidates"
              >
            </label>
          </div>
          <div class="upload-col description">
            <div>{{ uploadDescription || "Candidates can be uploaded from an XLSX (Excel) spreadsheet file" }}</div>
          </div>
        </div>
        <div
          v-if="templateUrl"
          class="upload-row"
        >
          <div class="upload-col tight button">
            <a
              :href="templateUrl"
              class="btn btn-sm btn-secondary"
            ><span class="glyphicons icon-left download align-middle" /><span class="align-middle">Download a template</span></a>
          </div>
          <div class="upload-col description">
            <div>{{ templateDescription || "Use this XLSX template to enter and upload candidates" }}</div>
          </div>
        </div>
      </div>
      <progress-indicator
        v-if="isUploading"
        :message="progressMessage"
        :progress="uploadProgress"
      />
    </template>
    <template #modal-footer>
      <loading-indicator
        v-if="isLoading"
      />
      <button
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
        type="button"
      >
        Close
      </button>
    </template>
  </ts-modal>
</template>

<script lang="ts" setup>
import axios from 'axios'
import Rails from '@rails/ujs'

import TsModal from "./ts-modal.vue"
import LoadingIndicator from './loading-indicator.vue'
import Alert from './alert.vue'
import {computed, ref, useTemplateRef, type PropType} from "vue";
import ProgressIndicator from "@/js/vue-components/progress-indicator.vue";
import {isNonEmptyString} from '../js-utils'

const props = defineProps({
  modalTitle: {default: 'Upload candidates', type: String},
  uploadUrl: {default: null, type: String as PropType<string | null>},
  templateUrl: {default: null, type: String as PropType<string | null>},
  uploadDescription: {default: null, type: String},
  templateDescription: {default: null, type: String},
  postData: {default: null, type: Object as () => Record<string, string>},
})

const modal = useTemplateRef<InstanceType<typeof TsModal>>("modal")
const importFileField = useTemplateRef<HTMLInputElement>("importFileField")

const isLoading = ref<boolean>(false)
const error = ref<string | null>(null)
const contextData = ref<Record<string, any> | null>(null)
const isUploading = ref<boolean>(false)
const uploadProgress = ref<number>(0)
const progressMessage = ref<string | null>(null)

const showWelcome = computed(() => !isLoading.value)

const showLoadingIndicator = (show: boolean): void => {
  isLoading.value = show
}

const showModal = (aContextData?: Record<string, any> | null): void => {
  contextData.value = aContextData || null
  error.value = null
  modal.value?.show()
}

const hideModal = (): void => {
  modal.value?.hide()
}

const uploadCandidates = (): void => {
  const uploadField = importFileField.value
  if (!uploadField || !uploadField.files || !isNonEmptyString(props.uploadUrl)) {
    return
  }

  const file = uploadField.files[0]
  if (file === null) {
    // IE 11 will fire change event when setting uploadField.value to ''
    // if file is null, then do not anything
    return
  }

  const formData = new FormData()
  formData.append('file', file)

  if (props.postData) {
    Object.entries(props.postData).forEach(([key, value]) => {
      formData.append(key, value)
    })
  }

  const aContextData = contextData.value
  if (aContextData) {
    for (const propName in Object.getOwnPropertyNames(aContextData)) {
      formData.append(propName, aContextData[propName])
    }
  }

  // IE 11 will fire change event again
  uploadField.value = ''

  showLoadingIndicator(true)
  isUploading.value = true
  uploadProgress.value = 0
  progressMessage.value = 'Uploading file ...'
  error.value = null

  axios.post(props.uploadUrl, formData, {
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'X-Requested-With': 'XMLHttpRequest',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'X-CSRF-Token': Rails.csrfToken()
    },
    onUploadProgress: (progressEvent) => {
      const total = progressEvent.total ?? progressEvent.loaded
      uploadProgress.value = Math.floor(progressEvent.loaded / total * 100)
      if (progressEvent.loaded === total) {
        progressMessage.value = 'Processing file ...'
      }
    }
  })
    .then((response) => {
      if (response.data) {
        if (response.data.url && response.data.url.length) {
          window.location.href = response.data.url
          return // keep loading indicator and everything in place
        } else if (response.data.error) {
          error.value = response.data.error
        } else {
          error.value = 'Invalid response from server'
        }
      }
      showLoadingIndicator(false)
      isUploading.value = false
    })
    .catch((error) => {
      error.value = error.message
      showLoadingIndicator(false)
      isUploading.value = false
    })
}

defineExpose({
  showModal,
  hideModal
})

</script>
